// Which open agent/cybo sessions to re-hydrate on a reconnect (#518).
//
// Two callers: the CLIENT's own reconnect (re-hydrate every open session) and an
// owner-DAEMON reconnect (re-hydrate only the sessions that daemon owns — the
// others' daemons never dropped, so re-fetching them is wasted work). Pure +
// exported so the scoping decision is unit-tested without app.svelte.ts.

export interface CatchUpAgent {
  agentId: string;
  daemonId?: string | null;
}

import { isSessionStreamKey } from "../session-progress.js";

// Filter the open agent ids down to the ones that should catch up.
//   - no `daemonId` (client reconnect) → ALL open sessions.
//   - a `daemonId` (that daemon came online) → sessions owned by it, PLUS any session
//     whose owner is unknown or null. A null daemonId is a solo/local session that
//     belongs to whatever single daemon is running. An unknown owner stays in: list_agents
//     leaves out ephemerals, and a failed roster refetch leaves a stale roster, so absence
//     is not proof the session is someone else's. The rule only EXCLUDES sessions provably
//     owned by a DIFFERENT daemon, plus (CYBORG-1182) an unrostered id a previous catch-up
//     already found gone, which every daemon coming online used to re-ask for.
export function agentIdsForDaemonCatchUp(
  openAgentIds: readonly string[],
  agents: readonly CatchUpAgent[],
  daemonId?: string | null,
  isTimelineGone: (agentId: string) => boolean = () => false,
): string[] {
  if (!daemonId) return [...openAgentIds];
  const ownerOf = new Map<string, string | null | undefined>(
    agents.map((a) => [a.agentId, a.daemonId]),
  );
  return openAgentIds.filter((id) => {
    if (!ownerOf.has(id)) return !isTimelineGone(id);
    const owner = ownerOf.get(id);
    return owner == null || owner === daemonId;
  });
}

// The two stores the read gate below consults, as narrow interfaces so the decision is
// unit-tested without agentStreamState / providerSubagentStore.
export interface CatchUpReadGate {
  /** The relay already refused this stream's timeline (AgentStreamState.timelineReadDenied). */
  isTimelineReadDenied(agentId: string): boolean;
  /** This stream key is a PROVIDER subagent, not an agent session. */
  isProviderSubagent(agentId: string): boolean;
  /** A catch-up read already answered not_found (AgentStreamState.timelineGone). */
  isTimelineGone(agentId: string): boolean;
}

// Of the in-scope ids, the ones catch-up may actually READ. Four kinds never are:
//   - one the relay already refused — reconnecting cannot grant access, and every
//     re-ask is a counted security denial (428 in 6 hours from one client);
//   - a provider subagent's stream — its history is `fetch_provider_subagent_timeline`
//     keyed by the PARENT, and no agent binding exists under a subagent id, so
//     `fetch_agent_timeline` can only answer not_found / an empty blind page;
//   - a WORK-SESSION stream key (`session:<id>`, sessionStreamKey) — SessionWorkView writes
//     its durable page into that synthetic key, so the key reaches here with entries and no
//     owner in workspaceState.agents, which agentIdsForDaemonCatchUp admits for EVERY
//     daemon's push. `fetch_agent_timeline` cannot resolve an agent for it (the
//     same reason loadOlderSessionWork exists), and the not_found it answers is not one of
//     the four denial codes the mark is set from — so it was re-asked on every reconnect forever;
//   - one a previous catch-up found gone (not_found): a deleted or archived agent's
//     conversation, which no reconnect brings back.
export function timelineIdsToCatchUp(agentIds: readonly string[], gate: CatchUpReadGate): string[] {
  return agentIds.filter(
    (id) =>
      !gate.isTimelineReadDenied(id) &&
      !gate.isTimelineGone(id) &&
      !gate.isProviderSubagent(id) &&
      !isSessionStreamKey(id),
  );
}

export const CATCH_UPS_PER_DAEMON = 2;

// DFU.6: at most CATCH_UPS_PER_DAEMON reads at once per owner (unknown and local share one), until
// the workspace changes; a failed read keeps its lane, and the first failure is rethrown at the end.
export async function runCatchUpsPerDaemon(
  agentIds: readonly string[],
  ownerOf: (agentId: string) => string | null | undefined,
  run: (agentId: string) => Promise<unknown>,
  stillCurrent: () => boolean = () => true,
): Promise<void> {
  const queues = new Map<string, string[]>();
  for (const agentId of agentIds) {
    const owner = ownerOf(agentId) ?? "";
    queues.set(owner, [...(queues.get(owner) ?? []), agentId]);
  }
  const failures: unknown[] = [];
  const lane = async (queue: string[]) => {
    for (let agentId = queue.shift(); agentId !== undefined; agentId = queue.shift()) {
      if (!stillCurrent()) return;
      await run(agentId).catch((err: unknown) => failures.push(err));
    }
  };
  const lanes = [...queues.values()].flatMap((queue) =>
    Array.from({ length: Math.min(CATCH_UPS_PER_DAEMON, queue.length) }, () => lane(queue)),
  );
  await Promise.all(lanes);
  if (failures.length > 0) throw failures[0];
}
